import React, { FC, useEffect, useMemo, useState } from 'react';
import { CircularProgress } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import COEHeaderComponent from '../../components/coe-header/coe-header.component';
import ProgramResultCard from '../../components/program-billing/program-result-card.component';
import useService from '../../hooks/service.hook';
import TeamsService from '../../services/teams.service';
import ProgramsService from '../../services/programs.service';
import { TEAM_DETAILS_STORAGE_KEYS, clearStoredValue, setStoredValue } from '../../helpers/cache.helpers';
import { trialLengthPhrase } from '../../helpers/billing.helpers';
import { Program } from '../../models/billing.models';

const CreateBasicTeamSuccessPage: FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const teamsService = useService(TeamsService);
  const programsService = useService(ProgramsService);
  const teamId = useMemo(() => searchParams.get('team_id') || '', [searchParams]);
  const [checking, setChecking] = useState(Boolean(teamId));
  const [teamActive, setTeamActive] = useState(false);
  const [createdTeamName, setCreatedTeamName] = useState<string | null>(null);
  const [createdTeamPlan, setCreatedTeamPlan] = useState<string | null>(null);
  const [program, setProgram] = useState<Program | null | undefined>(undefined);

  useEffect(() => {
    programsService
      .listPrograms()
      .then((response) => {
        const openPrograms = response.data.data || [];
        const match = openPrograms.find((item) => item.teams.some((team) => String(team.id) === String(teamId)));
        setProgram(match || null);
      })
      .catch(() => setProgram(null));
  }, []);

  const onTrial = program?.status === 'trialing';
  const trialLength = trialLengthPhrase(program?.trialDays);
  const teamName =
    program?.teams.find((team) => String(team.id) === String(teamId))?.name || createdTeamName || undefined;

  useEffect(() => {
    if (!teamId) {
      setChecking(false);
      setTeamActive(false);
      return;
    }

    let cancelled = false;
    setChecking(true);
    teamsService
      .getTeams()
      .then((response) => {
        if (cancelled) return;
        const teams = (response.data?.data || []) as Array<{
          id: number | string;
          status?: string;
          name?: string;
          plan?: string;
        }>;
        const match = teams.find((team) => String(team.id) === String(teamId));
        const active = Boolean(match && match.status === 'active');
        setCreatedTeamName(match?.name || null);
        setCreatedTeamPlan(match?.plan || null);
        setTeamActive(active);
        if (active) {
          setStoredValue(TEAM_DETAILS_STORAGE_KEYS.TEAM, String(teamId));
        } else if (getStoredMatches(teamId)) {
          clearStoredValue(TEAM_DETAILS_STORAGE_KEYS.TEAM);
        }
      })
      .catch(() => {
        if (!cancelled) setTeamActive(false);
      })
      .finally(() => {
        if (!cancelled) setChecking(false);
      });

    return () => {
      cancelled = true;
    };
  }, [teamId]);

  const openTeam = () => {
    if (teamActive && teamId) {
      setStoredValue(TEAM_DETAILS_STORAGE_KEYS.TEAM, String(teamId));
      navigate(`/teams/${teamId}/roster`, { state: { welcomeInvite: true } });
      return;
    }
    clearStoredValue(TEAM_DETAILS_STORAGE_KEYS.TEAM);
    navigate('/teams');
  };

  return (
    <div className={window.localStorage.getItem('sidedrawer') ? 'page-full' : 'page'} id="page">
      <COEHeaderComponent title={onTrial ? 'Free trial started' : 'Team created'} />
      {program === undefined ? (
        <ProgramResultCard pending eyebrow="Team created" title="Setting up your team…" />
      ) : (
        <ProgramResultCard
          eyebrow={onTrial ? "You're in" : 'Team created'}
          title={onTrial ? 'Your free trial is on' : `${teamName || 'Your team'} is ready`}
          subtitle={
            onTrial
              ? `${
                  trialLength ? `${trialLength.replace(/^./, (c) => c.toUpperCase())}, every feature` : 'Every feature'
                }, no card needed until you convert.`
              : 'Invite your coaches and athletes to get started.'
          }
          actions={
            checking ? (
              <CircularProgress size={24} />
            ) : (
              <>
                <button type="button" className="pb-btn pb-btn-secondary" onClick={() => navigate('/billing')}>
                  View billing
                </button>
                {teamActive ? (
                  <button type="button" className="pb-btn pb-btn-secondary" onClick={() => navigate('/teams')}>
                    My Teams
                  </button>
                ) : null}
                <button type="button" className="pb-btn pb-btn-primary" onClick={openTeam}>
                  {teamActive ? 'Open team' : 'Go to My Teams'}
                </button>
              </>
            )
          }
        >
          {program ? (
            <ul className="pb-result-rows">
              {teamName ? (
                <li>
                  <span>Team</span>
                  <b>{teamName}</b>
                </li>
              ) : null}
              <li>
                <span>Plan</span>
                <b>
                  {program.planName} · {program.billingInterval === 'year' ? 'Yearly' : 'Monthly'}
                </b>
              </li>
              {onTrial && program.trialEndsAt ? (
                <li>
                  <span>Free until</span>
                  <b>{dayjs(program.trialEndsAt).format('MMM D, YYYY')}</b>
                </li>
              ) : null}
              <li>
                <span>Members per team</span>
                <b>Up to {program.membersPerTeam}, coaches and athletes</b>
              </li>
            </ul>
          ) : createdTeamPlan === 'unlimited' ? (
            <ul className="pb-result-rows">
              {teamName ? (
                <li>
                  <span>Team</span>
                  <b>{teamName}</b>
                </li>
              ) : null}
              <li>
                <span>Plan</span>
                <b>CoeLab Unlimited</b>
              </li>
              <li>
                <span>Members</span>
                <b>Included</b>
              </li>
            </ul>
          ) : null}
        </ProgramResultCard>
      )}
    </div>
  );
};

const getStoredMatches = (teamId: string): boolean => {
  try {
    return window.localStorage.getItem(TEAM_DETAILS_STORAGE_KEYS.TEAM) === String(teamId);
  } catch {
    return false;
  }
};

export default CreateBasicTeamSuccessPage;
